/**
 * C-GTCC-VIEW-HW-GW2 — typed client for the GTCC view "Gateways".
 *
 * Four reads, no write. Two of them are endpoints the old console already
 * had; only `/v1/admin/gtcc/gw/**` is new, and that is the read-only module
 * `services/api/src/modules/gtcc/gw/` this ticket adds.
 *
 *   - `GET /v1/admin/gtcc/gw/endpoints` — the panel "Gateway-Endpunkte". The
 *     listener rows are the configuration the API process actually runs with;
 *     the traffic numbers are the gateway counters Prometheus scrapes.
 *   - `GET /v1/admin/gtcc/gw/parser-health` — the panel "Parser-Gesundheit".
 *   - `GET /v1/admin/gateway/rejections` — the panel "Abgelehnte Verbindungen"
 *     (surface `gateway`, SECURITY DEFINER, cross-tenant by design).
 *   - `GET /v1/admin/gateway/ingest` — the panel "Ingest /gpsdata (Legacy GT
 *     One)" including the configured limiter budgets and the last events.
 *
 * Read-only by construction: `gtccGet` wraps `apiGet` only. Nothing is derived
 * here — verdicts, statuses and ages arrive decided by the API, and a value
 * without a source arrives as `null` with a `reason`, which the view prints as
 * "nicht messbar" instead of a number nobody measured (Owner P8/P9/P10).
 *
 * E-GTCC-GW-WRITE-PORT1 — the predecessor `views/gw/GtccGatewayView.tsx` read
 * the same two platform routes and issued NO mutation (it imports `apiGet`
 * only). Porting its surfaces therefore adds no endpoint and no verb here; it
 * adds the payload field the predecessor's card needed (`unknownDevices`) and
 * the two constants that name the gate those routes are behind.
 */
import {
  gtccGet,
  type GtccMeasured,
  type GtccResult,
  type GtccStatus,
  type GtccVerdict,
} from "./gtcc-result.js";

/* --------------------------------------------------------------- paths */

const GW_BASE = "/v1/admin/gtcc/gw";

export const GW_ENDPOINTS_PATH = `${GW_BASE}/endpoints`;
export const GW_PARSER_HEALTH_PATH = `${GW_BASE}/parser-health`;
export const GW_REJECTIONS_PATH = "/v1/admin/gateway/rejections";
export const GW_INGEST_PATH = "/v1/admin/gateway/ingest";

/** The rejection route caps `windowDays` at 30 and `limit` at 200. */
export const GW_REJECTIONS_WINDOW_DAYS = 7;
export const GW_REJECTIONS_LIMIT = 25;

export const GW_REJECTIONS_QUERY_PATH = `${GW_REJECTIONS_PATH}?windowDays=${GW_REJECTIONS_WINDOW_DAYS}&limit=${GW_REJECTIONS_LIMIT}`;

/* ------------------------------------------------------------- the gate */

/**
 * E-GTCC-GW-WRITE-PORT1 — what a 403 from the two platform routes actually
 * means, so the view can name it instead of printing "Zugriff verweigert".
 *
 * Both `/v1/admin/gateway/**` routes call `requirePlatformOwner("gateway")`
 * (`services/api/src/modules/admin/gateway-ingest-diagnostics.routes.ts:80`,
 * `services/api/src/modules/admin/gateway-diagnostics.routes.ts:40`). That guard
 * throws `PlatformOwnerRequiredError` with the message `"Missing role:
 * platform.owner"` (`services/api/src/auth/platform-owner.ts:41`, role constant
 * `:46`) — the gate is a
 * ROLE, not a permission, and the string below is that role verbatim. The
 * surface is the guard's own argument.
 */
export const GW_REQUIRED_ROLE = "platform.owner";
export const GW_PLATFORM_SURFACE = "gateway";

/* ---------------------------------------------------------------- types */

/** One measurement of the envelope grammar, `modules/gtcc/gw/dto.ts`. */
export interface GwRowDto {
  readonly key: string;
  readonly label: string;
  readonly verdict: GtccVerdict;
  readonly valueText: string;
  readonly ageSeconds: number | null;
  /** Why there is no measurement — present exactly for `unmeasurable`. */
  readonly reason?: string;
}

/**
 * One configured receiving endpoint. The three numbers are `null` whenever
 * they cannot be attributed to this endpoint; `reason` then names why. A null
 * is therefore "not measurable", never "zero traffic".
 */
export interface GwEndpointDto {
  readonly key: string;
  readonly label: string;
  readonly address: string;
  readonly protocol: string;
  readonly verdict: GtccVerdict;
  readonly connectionsActive: number | null;
  readonly packetsPerMin: number | null;
  readonly reconnects24h: number | null;
  readonly reason: string | null;
}

export interface GwEndpointsDto extends GtccMeasured {
  readonly rows: ReadonlyArray<GwRowDto>;
  readonly endpoints: ReadonlyArray<GwEndpointDto>;
}

export interface GwParserHealthDto extends GtccMeasured {
  readonly rows: ReadonlyArray<GwRowDto>;
  readonly droppedRecords24h: number | null;
  readonly packets24h: number | null;
  readonly unknownImei24h: number | null;
  readonly clockSkewDropBoundSeconds: number | null;
}

/** Mirrors `PlatformRecentRejection`, `admin/gateway-diagnostics.service.ts:41`. */
export interface GwRejectionDto {
  readonly id: string;
  readonly tenantId: string | null;
  readonly imei: string;
  readonly reason: string;
  /** Absolute timestamps — the view never turns them into an age itself. */
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
  readonly attemptCount: number;
}

export interface GwRejectionsDto {
  readonly since: string;
  readonly counts: Readonly<Record<string, number>>;
  readonly recent: ReadonlyArray<GwRejectionDto>;
  /**
   * PAKET 003240 — `recent` is a bounded read; true means the window holds
   * more rejections than the list shows. Optional because a response from
   * before that change carries no such field, and absence must not read as
   * "complete". `counts` stays complete either way (aggregated in SQL).
   */
  readonly truncated?: boolean;
}

/** One token-bucket budget, `gpsdata-legacy/ingest-observability.ts:58`. */
export interface GwIngestBucketDto {
  readonly burst: number;
  readonly sustainedPerMin: number;
}

export interface GwIngestLimitsDto {
  readonly enabled: boolean;
  readonly trustedForwarderCount: number;
  readonly ip: GwIngestBucketDto;
  readonly trustedIp: GwIngestBucketDto;
  readonly imei: GwIngestBucketDto;
  readonly invalid: GwIngestBucketDto;
}

export interface GwIngestCountsDto {
  readonly accepted: number;
  readonly rateLimited: number;
  readonly invalidPayload: number;
  readonly unknownDevice: number;
}

export interface GwIngestForwarderDto {
  readonly ip: string;
  readonly trustedForwarder: boolean;
  readonly accepted: number;
  readonly rateLimited: number;
  readonly lastSeen: string;
  readonly lastLimited: string | null;
  readonly status: "ok" | "near_limit" | "limited";
}

export interface GwIngestRecentDto {
  readonly timestamp: string;
  readonly ip: string;
  /** Exactly what the device sent; `null` for an unparseable payload. */
  readonly identifier: string | null;
  readonly result: "accepted" | "rate_limited" | "invalid_payload" | "unknown_device";
  readonly reason: string | null;
  readonly trustedForwarder: boolean;
  /** Pipeline writes of an accepted event — `null` for every other result. */
  readonly positionsWritten: number | null;
  readonly telemetryWritten: number | null;
  readonly route: string;
}

/**
 * One persisted unknown device, transcribed field for field from
 * `UnknownDeviceRow` (`services/api/src/modules/gpsdata-legacy/unknown-devices.repo.ts:43`).
 *
 * This is the durable table of migration 0154, not the view's own bookkeeping:
 * `count` is the attempt counter the upsert maintains, `firstSeen`/`lastSeen`
 * are absolute timestamps from that table, and the route returns the top
 * `UNKNOWN_DEVICES_TOP_LIMIT` = 50 rows by attempt count (same file, `:28`).
 */
export interface GwUnknownDeviceDto {
  readonly deviceId: string;
  readonly firstSeen: string;
  readonly lastSeen: string;
  readonly count: number;
  readonly lastSourceIp: string | null;
  readonly lastPath: string | null;
}

/** Bounded persistent counter response; `truncated` means another device exists. */
export interface GwUnknownDevicesDto {
  readonly rows: ReadonlyArray<GwUnknownDeviceDto>;
  readonly truncated: boolean;
}

/** Transcribed from `IngestObservabilitySnapshot`, same file, line 389. */
export interface GwIngestDto {
  readonly route: string;
  readonly enabled: boolean;
  readonly generatedAt: string;
  readonly dataSource: "memory" | "database" | "combined";
  readonly limits: GwIngestLimitsDto | null;
  readonly retention: { readonly inMemoryWindowHours: number; readonly recentEventsCap: number };
  readonly status: "ok" | "near_limit" | "limited";
  readonly windows: {
    readonly "5m": GwIngestCountsDto;
    readonly "1h": GwIngestCountsDto;
    readonly "24h": GwIngestCountsDto;
  };
  readonly forwarders: ReadonlyArray<GwIngestForwarderDto>;
  /** True if the ingest snapshot held more forwarder IPs than this page shows. */
  readonly forwardersTruncated: boolean;
  readonly recent: ReadonlyArray<GwIngestRecentDto>;
  /**
   * The route's own counter, added on top of the snapshot by
   * `gateway-ingest-diagnostics.routes.ts:84` (`await unknownDevices()`) and
   * returned as the field `unknownDevices` at `:85`.
   *
   * OPTIONAL on purpose. An older API on the other side of this deck does not
   * send the field at all, and an absent field is "not read", never "no unknown
   * device": the route's own docblock forbids the card from claiming the latter
   * when the counter could not be read, so the view distinguishes `undefined`
   * (nicht messbar) from `[]` (measured, empty).
   */
  readonly unknownDevices?: GwUnknownDevicesDto;
}

/* -------------------------------------------------------------- fetchers */

export function fetchGwEndpoints(signal?: AbortSignal): Promise<GtccResult<GwEndpointsDto>> {
  return gtccGet<GwEndpointsDto>(GW_ENDPOINTS_PATH, signal);
}

export function fetchGwParserHealth(signal?: AbortSignal): Promise<GtccResult<GwParserHealthDto>> {
  return gtccGet<GwParserHealthDto>(GW_PARSER_HEALTH_PATH, signal);
}

export function fetchGwRejections(signal?: AbortSignal): Promise<GtccResult<GwRejectionsDto>> {
  return gtccGet<GwRejectionsDto>(GW_REJECTIONS_QUERY_PATH, signal);
}

export function fetchGwIngest(signal?: AbortSignal): Promise<GtccResult<GwIngestDto>> {
  return gtccGet<GwIngestDto>(GW_INGEST_PATH, signal);
}

/* ----------------------------------------------------------- query keys */

/*
 * Prefix convention of the GTCC query-key inventory: `["gtcc", <viewkey>, …]`.
 * The view boundary cancels and clears by exactly this prefix. The last two
 * keys say `rejection-log` / `ingest-snapshot` rather than `rejections` /
 * `ingest` because the legacy module `views/gw/GtccGatewayView.tsx` still owns
 * `["gtcc","gw","rejections"]` and `["gtcc","gw","ingest"]` on disk and caches
 * a different shape under them — the same precedent as `HW_POOL_KEY`.
 */
export const GW_ENDPOINTS_KEY = ["gtcc", "gw", "endpoints"] as const;
export const GW_PARSER_HEALTH_KEY = ["gtcc", "gw", "parser-health"] as const;
export const GW_REJECTIONS_KEY = ["gtcc", "gw", "rejection-log"] as const;
export const GW_INGEST_KEY = ["gtcc", "gw", "ingest-snapshot"] as const;

export const GW_QUERY_KEYS = {
  endpoints: GW_ENDPOINTS_KEY,
  parserHealth: GW_PARSER_HEALTH_KEY,
  rejections: GW_REJECTIONS_KEY,
  ingest: GW_INGEST_KEY,
} as const;

/** Re-exported so consumers of this module need one import, not two. */
export type { GtccResult, GtccStatus, GtccVerdict };
